import { useState } from 'react';
import { Button } from 'reactstrap';
import * as FileSaver from 'file-saver';
import { capitalize } from 'lodash';
import { centerOfMass } from '@turf/turf';

import { usePortfolioEvaluatorContext } from '../../contexts/PortfolioEvaluatorContext';
import { captureEvent } from '../../utils/analytics';
import { toCsv } from '../../utils/csv';
import { useAreaUnits } from '../../contexts/UnitContext';
import { layerIdForTileset } from '../../utils/tilesets';

const useColumns = () => {
  const { tilesets } = usePortfolioEvaluatorContext();
  const { areaFormatter, longUnitName } = useAreaUnits();

  return [
    { displayString: 'Plot', accessor: (feature) => feature.properties.name },
    {
      displayString: 'State',
      accessor: (feature) =>
        feature.properties.usState?.abbreviation || 'Unknown State',
    },
    {
      displayString: `Total ${capitalize(longUnitName)}`,
      accessor: (feature) => feature.properties.area,
      format: areaFormatter,
    },
    {
      displayString: 'Lat/Lng',
      accessor: (feature) => centerOfMass(feature).geometry.coordinates,
      format: (coordinates) =>
        coordinates && `${coordinates[1]},${coordinates[0]}`,
    },
    // Suitability values:
    ...tilesets.map((tileset) => ({
      displayString: `Suitable ${longUnitName} for ${tileset.displayName}`,
      accessor: (feature) =>
        feature.properties.suitability[layerIdForTileset(tileset.id)],
      format: areaFormatter,
    })),
  ];
};

const ExportButton = () => {
  const { features, suitabilityData } = usePortfolioEvaluatorContext();
  const [exportingData, setExportingData] = useState(false);
  const columns = useColumns();

  const onClick = async () => {
    try {
      setExportingData(true);

      const data = features.map((feature, i) => ({
        ...feature,
        properties: {
          ...feature.properties,
          suitability: suitabilityData?.[i],
        },
      }));

      const csvData = toCsv(data, columns);
      const csv = new Blob([csvData], { type: 'text/csv' });
      FileSaver.saveAs(csv, 'portfolio-data.csv');
    } catch (err) {
      console.error(err);
    } finally {
      setExportingData(false);
      captureEvent('portfolio-data-exported');
    }
  };

  const disabled = features?.length === 0 || exportingData;

  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      className="w-100"
      size="sm"
      outline
      color="secondary"
    >
      {exportingData ? 'Exporting...' : 'Export Portfolio Evaluation'}
    </Button>
  );
};

export default ExportButton;
